import { createContext } from "react";
import URLParamService from "@/services/URLParamService";

type PromoCurrency = {
  id: number;
  orgUuid: string;
  promoUuid: string;
  createdAt: string;
  ISOCode: string;
  symbol: string;
  exchangeRate: number;
  isPrimary: boolean;
};

const isValidCurrencyISO = (code: string): boolean => {
  try {
    new Intl.NumberFormat("en", { style: "currency", currency: code });
    return true;
  } catch {
    return false;
  }
};

export type CurrencyContextType = {
  userCurrency: string;
  currentActiveExRate: PromoCurrency;
  convertToUserCurrency: (cashValue: number) => any;
  convertToPrimaryCurrency: (userCurrencyValue: number) => number;
  symbol: string;
};

export const CurrencyContext = createContext<CurrencyContextType | null>(null);

export const CurrencyProvider = ({
  children,
  widgetData,
}: {
  children: React.ReactNode;
  widgetData: any;
}) => {
  const queryParams = URLParamService.getQueryParams();
  const userCurrency = queryParams.currency;
  const exRates = widgetData?.exRates;

  const normalizedRates: PromoCurrency[] = Array.isArray(exRates) ? exRates : [];
  const upperUserCurrency = userCurrency ? userCurrency.toUpperCase() : undefined;
  const existsInExRates = upperUserCurrency
    ? normalizedRates.some((r: PromoCurrency) => r.ISOCode === upperUserCurrency)
    : false;
  const shouldUseUserCurrency =
    !!upperUserCurrency && (isValidCurrencyISO(userCurrency) || existsInExRates);
  const userSelectedExRate = shouldUseUserCurrency
    ? normalizedRates.find((exRate: PromoCurrency) => exRate.ISOCode === upperUserCurrency)
    : undefined;
  const primaryExRate = normalizedRates.find((exRate: PromoCurrency) => exRate.isPrimary);
  const usdExRate = normalizedRates.find((exRate: PromoCurrency) => exRate.ISOCode === "USD");
  const currentActiveExRate = userSelectedExRate || primaryExRate || usdExRate || normalizedRates[0];

  const convertToUserCurrency = (cashValue: number) => {
    const rate = currentActiveExRate?.exchangeRate;
    const numericRate = typeof rate === "string" ? parseFloat(rate) : rate;
    const numericCashValue = typeof cashValue === "number" ? cashValue : Number(cashValue);

    if (!Number.isFinite(numericRate) || !Number.isFinite(numericCashValue)) {
      return Number.isFinite(numericCashValue) ? numericCashValue : 0;
    }

    return numericRate * numericCashValue;
  };

  const convertToPrimaryCurrency = (userCurrencyValue: number) => {
    const exchangeRateRaw = currentActiveExRate?.exchangeRate;
    const exchangeRate =
      typeof exchangeRateRaw === "string" ? parseFloat(exchangeRateRaw) : exchangeRateRaw;
    const numericUserCurrencyValue =
      typeof userCurrencyValue === "number" ? userCurrencyValue : Number(userCurrencyValue);

    if (!Number.isFinite(exchangeRate) || exchangeRate === 1) return numericUserCurrencyValue;
    if (!Number.isFinite(numericUserCurrencyValue)) return 0;

    return numericUserCurrencyValue / exchangeRate;
  };

  return (
    <CurrencyContext.Provider
      value={{
        userCurrency,
        currentActiveExRate,
        convertToUserCurrency,
        convertToPrimaryCurrency,
        symbol: currentActiveExRate?.symbol || currentActiveExRate?.ISOCode || "",
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
